import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Mymatch } from '../../assets/svg';
import FormatterInstance from '../../i18n/formatter';
import theme from '../../styled-components/dark';
import type { OutcomeStatus } from '../../types/history';
import { getOutcomeIconFromStatus } from '../../utils/outcome';
import CollapseListItem from '../CollapseListItem';
import ExpandArrow from '../ExpandArrow/ExpandArrow';
import HistoryItemOutcomeMyMatchLine from '../HistoryItemOutcomeMyMatchLine';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0.2em;
`;

const OutcomesWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.34em;
  margin-bottom: 0.25em;
  width: 100%;
`;

const StatusIcon = styled.div`
  width: 0.93em;
  height: 0.93em;
  overflow: visible !important;
`;

const StyledMyMatchIcon = styled(Mymatch)`
  font-size: 0.8em;
  color: ${theme.History.outcome.mymatchContent.default};
  width: 6em;
  height: 2em;
`;

const ArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.2em;

  width: 2em;
  height: 2em;
  background: ${theme.History.betslip.outcome.arrowBackground};
  border-radius: 100%;

  cursor: pointer;
`;

type ExpandArrowPropsType = {
  $isExpand: boolean;
};

const StyledExpandArrow = styled(ExpandArrow)<ExpandArrowPropsType>`
  height: 0.52em;

  transition: transform 250ms ease-in-out;

  ${({ $isExpand }) =>
    $isExpand
      ? css`
          transform: rotate(0deg) translateY(-0.05em);
        `
      : css`
          transform: rotate(180deg) translateY(-0.05em);
        `}
`;

const HeaderLeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3em;
`;

const HeaderRightSide = styled.div`
  display: flex;
  align-items: center;
`;

const OddsWrapper = styled.div`
  display: flex;

  align-items: flex-start;
`;

type OddsPropsType = {
  $isInitial?: boolean;
};

const Odds = styled.div<OddsPropsType>`
  font-size: 1em;
  font-weight: bold;
  border-radius: 0.4em;
  padding: 0.14em 0.33em;
  margin-left: 0.33em;

  ${({ $isInitial }) =>
    $isInitial
      ? css`
          text-decoration: line-through;
          color: ${theme.History.outcome.content.initialOddColor};
          background: ${theme.History.outcome.content.initialOddBackgroundColor};
        `
      : css`
          color: ${theme.History.outcome.content.oddColor};
          background: ${theme.History.outcome.content.oddBackgroundColor};
        `}
`;

type SubOutcomeType = {
  outcomeLabel: string;
  outcomeStatus: OutcomeStatus;
  betTitle: string;
  outcomeId?: number;
};

type HistoryItemOutcomeMyMatchContentProps = {
  className?: string;
  subOutcomes: Array<SubOutcomeType>;
  outcomeStatus: OutcomeStatus;
  isSimpleBet?: boolean;
  isUserPrefsCompact?: boolean;
  noBreakLine?: boolean;
  odds?: number;
  initialOdds?: number;
};

const HistoryItemOutcomeMyMatchContent = ({
  className,
  subOutcomes,
  outcomeStatus,
  isSimpleBet,
  isUserPrefsCompact,
  noBreakLine,
  odds,
  initialOdds,
}: HistoryItemOutcomeMyMatchContentProps) => {
  const Icon = useMemo(() => getOutcomeIconFromStatus(outcomeStatus), [outcomeStatus]);
  const isCompactable = useMemo(() => isSimpleBet && subOutcomes.length > 2, [isSimpleBet, subOutcomes]);

  const [isCompact, setIsCompact] = useState(isUserPrefsCompact);

  const onClickExpand = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsCompact(!isCompact);
    },
    [isCompact],
  );

  useEffect(() => {
    setIsCompact(isUserPrefsCompact);
  }, [isUserPrefsCompact]);

  const collapseItemKey = useMemo(
    () => (isUserPrefsCompact ? 'compact-history:true' : 'compact-history:false'),
    [isUserPrefsCompact],
  );

  const isOpened = useMemo(() => !isCompactable || !isCompact, [isCompactable, isCompact]);

  return (
    <Wrapper className={className}>
      <Header onClick={isCompactable ? onClickExpand : undefined}>
        <HeaderLeftSide>
          {isCompactable ? (
            <ArrowWrapper>
              <StyledExpandArrow $isExpand={!isCompact} />
            </ArrowWrapper>
          ) : null}
          <StatusIcon as={Icon} />
          <StyledMyMatchIcon />
        </HeaderLeftSide>
        <HeaderRightSide>
          {odds ? (
            <OddsWrapper>
              {initialOdds ? <Odds $isInitial>{FormatterInstance.formatOdd(initialOdds)}</Odds> : null}
              <Odds>{FormatterInstance.formatOdd(odds)}</Odds>
            </OddsWrapper>
          ) : null}
        </HeaderRightSide>
      </Header>
      {/* Add `key` to force rerender
       * https://github.com/oblador/react-native-collapsible/issues/420#issuecomment-1019668713
       */}
      <CollapseListItem key={collapseItemKey} isOpened={isOpened}>
        <OutcomesWrapper>
          {subOutcomes.map(
            ({ outcomeLabel: subOutcomeLabel, outcomeStatus: subOutcomeStatus, betTitle, outcomeId }, idx) => {
              return (
                <HistoryItemOutcomeMyMatchLine
                  key={outcomeId ?? idx}
                  outcomeLabel={subOutcomeLabel}
                  outcomeStatus={subOutcomeStatus}
                  betTitle={betTitle}
                  showCircle={subOutcomes.length > 1}
                  noBreakLine={noBreakLine}
                  outcomeId={outcomeId}
                />
              );
            },
          )}
        </OutcomesWrapper>
      </CollapseListItem>
    </Wrapper>
  );
};

export default React.memo(HistoryItemOutcomeMyMatchContent);
